import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * A field's debounced commit, which can also be run at once.
 *
 * Debouncing keeps a field from reporting every keystroke, but a field that
 * only reports when its timer fires loses the last edit to any click that
 * comes sooner: the space-time field in rental setup (500 ms) saved a slot
 * definition with no space-time at all when "Legg til" followed the typing
 * too closely. The fields flush on blur, and a click always blurs first.
 */
const useDebouncedCommit = (debounceMs, commit) => {
    const timer = useRef(null);
    const pending = useRef(null);
    const [isPending, setIsPending] = useState(false);
    // The latest commit, so a timer set before a re-render still reports
    // through the current onUpdate.
    const commitRef = useRef(commit);
    commitRef.current = commit;
    const flush = useCallback(() => {
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = null;
        const next = pending.current;
        pending.current = null;
        setIsPending(false);
        if (next)
            commitRef.current(next.value);
    }, []);
    const schedule = useCallback((value) => {
        if (debounceMs <= 0) {
            commitRef.current(value);
            return;
        }
        pending.current = { value };
        setIsPending(true);
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = setTimeout(flush, debounceMs);
    }, [debounceMs, flush]);
    // An unmount with an edit still waiting (a dialog closed by its own button)
    // reports it rather than dropping it.
    useEffect(() => flush, [flush]);
    return { schedule, flush, isPending };
};
export { useDebouncedCommit };
//# sourceMappingURL=useDebouncedCommit.js.map